import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, ArrowRight, ArrowLeft, Sparkles } from 'lucide-react';
import { trackEvent } from '../lib/analytics';
import { STARTER_MODULES, OPTIONAL_MODULES } from '../lib/optionalModules';

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1 = identity, 2 = modules
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    company_name: '',
  });
  const [selectedOptional, setSelectedOptional] = useState([]);

  useEffect(() => {
    trackEvent('signup_started');
  }, []);

  const toggleOptional = (key) => {
    setSelectedOptional((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const handleNextStep = (e) => {
    e.preventDefault();
    if (!formData.first_name || !formData.last_name || !formData.email || !formData.password) {
      toast.error('Merci de remplir tous les champs obligatoires');
      return;
    }
    if (formData.password.length < 6) {
      toast.error('Le mot de passe doit faire au moins 6 caractères');
      return;
    }
    setStep(2);
    trackEvent('signup_step2_reached');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register({ ...formData, enabled_optional_modules: selectedOptional });
      toast.success('Compte créé avec succès');
      navigate('/onboarding/invite-team');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du compte');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="register-page">
      <div className="w-full max-w-2xl">
        <div className="text-center mb-8">
          <img src="/logo-devisgo.png" alt="Devis.Go" className="h-24 mx-auto mb-2" />
          <p className="text-slate-500 mt-2">Devis, Factures & CRM</p>
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-center gap-3 mb-6">
          <div className={`flex items-center gap-2 ${step >= 1 ? 'text-orange-600' : 'text-slate-400'}`}>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${step >= 1 ? 'bg-orange-500 text-white' : 'bg-slate-200'}`}>
              1
            </div>
            <span className="text-sm font-medium">Vos infos</span>
          </div>
          <div className={`w-12 h-px ${step >= 2 ? 'bg-orange-300' : 'bg-slate-200'}`} />
          <div className={`flex items-center gap-2 ${step >= 2 ? 'text-orange-600' : 'text-slate-400'}`}>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${step >= 2 ? 'bg-orange-500 text-white' : 'bg-slate-200'}`}>
              2
            </div>
            <span className="text-sm font-medium">Vos modules</span>
          </div>
        </div>

        <Card className="border-slate-200">
          {step === 1 && (
            <>
              <CardHeader className="space-y-1">
                <CardTitle className="font-outfit text-2xl">Créer un compte</CardTitle>
                <CardDescription>Remplissez le formulaire pour créer votre compte</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleNextStep} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="first_name">Prénom</Label>
                      <Input
                        id="first_name"
                        placeholder="Jean"
                        value={formData.first_name}
                        onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                        required
                        data-testid="register-firstname-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="last_name">Nom</Label>
                      <Input
                        id="last_name"
                        placeholder="Dupont"
                        value={formData.last_name}
                        onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                        required
                        data-testid="register-lastname-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company_name">Nom de l&apos;entreprise (optionnel)</Label>
                    <Input
                      id="company_name"
                      placeholder="Ma Société SARL"
                      value={formData.company_name}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                      data-testid="register-company-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="nom@entreprise.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      data-testid="register-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Mot de passe</Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      minLength={6}
                      data-testid="register-password-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-slate-900 hover:bg-slate-800"
                    data-testid="register-next-btn"
                  >
                    Continuer <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </form>
                <div className="mt-6 text-center text-sm text-slate-500">
                  Déjà un compte ?{' '}
                  <Link to="/login" className="text-blue-600 hover:underline font-medium" data-testid="login-link">
                    Se connecter
                  </Link>
                </div>
              </CardContent>
            </>
          )}

          {step === 2 && (
            <>
              <CardHeader className="space-y-1">
                <CardTitle className="font-outfit text-2xl flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-orange-500" />
                  Choisissez vos modules
                </CardTitle>
                <CardDescription>
                  Le pack Starter est inclus. Cochez les modules supplémentaires que vous souhaitez utiliser —
                  vous pourrez en activer ou en désactiver à tout moment depuis vos paramètres.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Starter pack — always included */}
                  <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-orange-50/60 to-white p-5">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-slate-900">Pack Starter</h3>
                        <p className="text-xs text-slate-500 mt-0.5">Toutes les fondations, toujours incluses</p>
                      </div>
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-100 px-2.5 py-1 rounded-full">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Inclus
                      </span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-1.5 gap-x-3" data-testid="starter-modules">
                      {STARTER_MODULES.map((m) => (
                        <div key={m.key} className="flex items-center gap-1.5 text-xs text-slate-600">
                          <CheckCircle2 className="w-3 h-3 text-green-500 flex-shrink-0" />
                          {m.label}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Optional modules */}
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-2">Modules optionnels</h3>
                    <p className="text-xs text-slate-500 mb-3">
                      Cochez ceux que vous souhaitez activer dès le départ.
                    </p>
                    <div className="space-y-2" data-testid="optional-modules">
                      {OPTIONAL_MODULES.map((m) => {
                        const checked = selectedOptional.includes(m.key);
                        return (
                          <label
                            key={m.key}
                            htmlFor={`optional-${m.key}`}
                            className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition ${
                              checked
                                ? 'border-orange-300 bg-orange-50/60'
                                : 'border-slate-200 bg-white hover:border-slate-300'
                            }`}
                            data-testid={`optional-module-${m.key}`}
                          >
                            <input
                              id={`optional-${m.key}`}
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleOptional(m.key)}
                              className="mt-1 w-4 h-4 accent-orange-500 cursor-pointer"
                              data-testid={`optional-checkbox-${m.key}`}
                            />
                            <div className="flex-1">
                              <p className="font-medium text-sm text-slate-900">{m.label}</p>
                              <p className="text-xs text-slate-500 mt-0.5">{m.description}</p>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setStep(1)}
                      data-testid="register-back-btn"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" /> Retour
                    </Button>
                    <Button
                      type="submit"
                      className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                      disabled={loading}
                      data-testid="register-submit-btn"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Création...
                        </>
                      ) : (
                        <>
                          Créer mon compte <ArrowRight className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  </div>
                  <p className="text-center text-xs text-slate-400">
                    Vous pourrez activer ou désactiver chaque module plus tard dans vos paramètres.
                  </p>
                </form>
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </div>
  );
};

export default Register;
